<template>
  <FlashNotifications ref="flash" />

  <!-- Remove confirmation modal -->
  <Transition name="modal">
    <div v-if="showRemoveModal"
      class="fixed inset-0 z-50 flex items-center justify-center p-4"
      @click.self="showRemoveModal = false">
      <div class="absolute inset-0 bg-black/40 backdrop-blur-sm"></div>
      <div class="relative bg-white dark:bg-[#1C2128] rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 max-w-sm w-full p-6 modal-card">
        <div class="w-12 h-12 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mx-auto mb-4">
          <svg class="w-6 h-6 text-red-500" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9"/>
          </svg>
        </div>
        <h3 class="text-lg font-bold text-gray-900 dark:text-white text-center mb-2">Remove integration?</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 text-center mb-6">
          Your payment page URL will be removed and external payments will be disabled on all events.
        </p>
        <div class="flex gap-3">
          <button @click="showRemoveModal = false"
            class="flex-1 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors">
            Cancel
          </button>
          <button @click="removeIntegration" :disabled="removing"
            class="flex-1 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-60 rounded-lg transition-colors flex items-center justify-center gap-2">
            <svg v-if="removing" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
            </svg>
            {{ removing ? 'Removing...' : 'Remove' }}
          </button>
        </div>
      </div>
    </div>
  </Transition>

  <!-- Page -->
  <div class="min-h-screen bg-gray-50 dark:bg-[#0d1117]">

    <!-- ══ MOBILE ══ -->
    <div class="sm:hidden">
      <div class="sticky top-0 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-100 dark:border-gray-700">
        <div class="flex items-center justify-between px-4 h-14">
          <div class="flex items-center gap-2">
            <RouterLink to="/integrations" data-turbo-prefetch="false"
              class="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors -ml-1">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"/>
              </svg>
            </RouterLink>
            <h1 class="text-[20px] font-bold text-gray-900 dark:text-white tracking-tight leading-none">External Payments</h1>
          </div>
        </div>
      </div>

      <div class="px-4 pt-4 pb-28 space-y-4">

        <!-- Mobile skeleton -->
        <div v-if="loading" class="space-y-4 animate-pulse">
          <div class="bg-white dark:bg-[#1C2128] rounded-2xl border border-gray-100 dark:border-gray-700 p-5 space-y-3">
            <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
            <div class="h-3 bg-gray-100 dark:bg-gray-800 rounded w-3/4"></div>
            <div class="h-10 bg-gray-100 dark:bg-gray-800 rounded-xl"></div>
            <div class="h-10 bg-gray-200 dark:bg-gray-700 rounded-xl w-1/3"></div>
          </div>
        </div>

        <!-- Mobile error -->
        <div v-else-if="error" class="flex flex-col items-center justify-center py-20 text-center">
          <div class="w-14 h-14 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
            <svg class="w-7 h-7 text-red-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/>
            </svg>
          </div>
          <p class="font-semibold text-gray-900 dark:text-white mb-1">Something went wrong</p>
          <p class="text-sm text-gray-400 mb-4">{{ error }}</p>
          <button @click="fetchIntegration" class="text-blue-600 dark:text-blue-400 font-semibold text-sm">Try again</button>
        </div>

        <!-- Mobile content -->
        <template v-else>

          <!-- Status badge -->
          <div class="flex items-center gap-2 px-1">
            <span v-if="integration" class="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
              <span class="w-2 h-2 rounded-full bg-emerald-500"></span>
              Connected
            </span>
            <span v-else class="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-400 dark:text-gray-500">
              <span class="w-2 h-2 rounded-full bg-gray-300 dark:bg-gray-600"></span>
              Not connected
            </span>
          </div>

          <!-- Form card -->
          <div class="bg-white dark:bg-[#1C2128] rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div class="px-4 py-4 border-b border-gray-50 dark:border-gray-800">
              <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Payment Page URL</p>
            </div>
            <div class="px-4 py-4 space-y-3">
              <p class="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                {{ parseLocale(I18N.application.integrations.external_payments.subheading1, { application_name: APPLICATION_NAME }) }}
              </p>
              <div class="relative">
                <input v-model="url" type="url" placeholder="https://your-payment-page.com"
                  class="w-full px-3 py-2.5 pr-9 text-sm border rounded-xl font-mono bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow"
                  :class="urlError ? 'border-red-400 dark:border-red-500' : 'border-gray-200 dark:border-gray-700'"/>
                <svg v-if="urlError" class="absolute right-2.5 top-3 w-4 h-4 text-red-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
                </svg>
                <svg v-else-if="urlLooksValid" class="absolute right-2.5 top-3 w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/>
                </svg>
              </div>
              <p v-if="urlError" class="text-xs text-red-500 dark:text-red-400">{{ urlError }}</p>
            </div>
            <div class="px-4 pb-4 flex items-center gap-3">
              <button @click="submitUrl" :disabled="saving || !url.trim()"
                class="px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-xl transition-colors flex items-center gap-2">
                <svg v-if="saving" class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                </svg>
                {{ saving ? 'Saving...' : (integration ? I18N.application.integrations.external_payments.update : I18N.application.integrations.external_payments.submit) }}
              </button>
              <button v-if="integration" @click="showRemoveModal = true"
                class="px-4 py-2.5 text-sm font-medium text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors">
                Remove
              </button>
            </div>
          </div>

          <!-- Tutorial card -->
          <div class="bg-white dark:bg-[#1C2128] rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div class="px-4 py-4 border-b border-gray-50 dark:border-gray-800">
              <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tutorial</p>
            </div>
            <a href="https://www.youtube.com" target="_blank" class="block relative group">
              <img src="/images/youtube_thumbnail.jpg" alt="Tutorial video" class="w-full object-cover"/>
              <div class="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                <div class="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                  <svg class="w-5 h-5 text-red-600 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                </div>
              </div>
            </a>
          </div>

        </template>
      </div>
    </div>
    <!-- ══ END MOBILE ══ -->


    <!-- ══ DESKTOP ══ -->
    <div class="hidden sm:flex sm:flex-col sm:h-screen">

      <!-- Header -->
      <div class="flex-shrink-0 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700">
        <div class="flex items-center justify-between px-6 py-3.5">
          <div class="flex items-center gap-3">
            <RouterLink to="/integrations" data-turbo-prefetch="false"
              class="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"/>
              </svg>
              Integrations
            </RouterLink>
            <div class="h-4 w-px bg-gray-200 dark:bg-gray-700"></div>
            <div>
              <div class="flex items-center gap-2.5">
                <h1 class="text-xl font-bold text-gray-900 dark:text-white">External Payments</h1>
                <span v-if="!loading && integration"
                  class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400">
                  <span class="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                  Connected
                </span>
              </div>
              <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                {{ I18N.application.integrations.external_payments.integrate_external_payments }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-y-auto px-6 py-6">
        <div class="max-w-3xl mx-auto space-y-4">

          <!-- Skeleton -->
          <template v-if="loading">
            <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 p-6 animate-pulse space-y-4">
              <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
              <div class="h-3 bg-gray-100 dark:bg-gray-800 rounded w-2/3"></div>
              <div class="h-10 bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
              <div class="h-10 bg-gray-200 dark:bg-gray-700 rounded-lg w-28"></div>
            </div>
          </template>

          <!-- Error -->
          <template v-else-if="error">
            <div class="flex flex-col items-center justify-center py-32 text-center">
              <div class="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
                <svg class="w-8 h-8 text-red-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/>
                </svg>
              </div>
              <h3 class="text-base font-semibold text-gray-900 dark:text-white mb-1">Something went wrong</h3>
              <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">{{ error }}</p>
              <button @click="fetchIntegration" class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors">Try again</button>
            </div>
          </template>

          <!-- Loaded -->
          <template v-else>

            <!-- Two-column layout: form + tutorial -->
            <div class="grid grid-cols-1 lg:grid-cols-5 gap-4">

              <!-- Form card -->
              <div class="lg:col-span-3 bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
                <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
                  <h2 class="text-sm font-semibold text-gray-900 dark:text-white">Configuration</h2>
                  <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Set the URL where clients will be sent to complete payment.</p>
                </div>

                <div class="px-6 py-5 space-y-4">
                  <div class="space-y-1.5">
                    <label class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {{ I18N.application.integrations.external_payments.payment_page_url }}
                    </label>
                    <p class="text-xs text-gray-400 dark:text-gray-500 leading-relaxed">
                      {{ parseLocale(I18N.application.integrations.external_payments.subheading1, { application_name: APPLICATION_NAME }) }}
                    </p>
                    <div class="relative mt-2">
                      <input v-model="url" type="url" placeholder="https://your-payment-page.com"
                        class="w-full px-3 py-2 pr-9 text-sm border rounded-lg font-mono bg-white dark:bg-gray-800/80 text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow"
                        :class="urlError ? 'border-red-400 dark:border-red-500' : 'border-gray-200 dark:border-gray-700'"/>
                      <svg v-if="urlError" class="absolute right-2.5 top-2.5 w-4 h-4 text-red-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
                      </svg>
                      <svg v-else-if="urlLooksValid" class="absolute right-2.5 top-2.5 w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/>
                      </svg>
                    </div>
                    <p v-if="urlError" class="text-xs text-red-500 dark:text-red-400">{{ urlError }}</p>
                  </div>
                </div>

                <div class="px-6 pb-6 flex items-center gap-3">
                  <button @click="submitUrl" :disabled="saving || !url.trim()"
                    class="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg transition-colors shadow-sm">
                    <svg v-if="saving" class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                    </svg>
                    {{ saving ? 'Saving...' : (integration ? I18N.application.integrations.external_payments.update : I18N.application.integrations.external_payments.submit) }}
                  </button>
                  <button v-if="integration" @click="showRemoveModal = true"
                    class="px-4 py-2 text-sm font-medium text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors">
                    Remove integration
                  </button>
                </div>
              </div>

              <!-- Tutorial card -->
              <div class="lg:col-span-2 bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
                <div class="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                  <h2 class="text-sm font-semibold text-gray-900 dark:text-white">Tutorial</h2>
                  <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Watch the setup guide to get started.</p>
                </div>
                <a href="https://www.youtube.com" target="_blank" class="block relative group">
                  <img src="/images/youtube_thumbnail.jpg" alt="Setup tutorial" class="w-full object-cover"/>
                  <div class="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <div class="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                      <svg class="w-6 h-6 text-red-600 ml-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </div>
                  </div>
                </a>
              </div>
            </div>

            <!-- API docs -->
            <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
              <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
                <h2 class="text-sm font-semibold text-gray-900 dark:text-white">API Integration Guide</h2>
                <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                  Use our API to access payment endpoints and integrate external payment flows with your events.
                </p>
              </div>
              <div class="px-6 py-5">
                <img src="/images/flowchart1.png" alt="API flowchart" class="w-full max-h-[500px] object-contain rounded-lg"/>
              </div>
            </div>

          </template>
        </div>
      </div>
    </div>
    <!-- ══ END DESKTOP ══ -->

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { RouterLink } from 'vue-router'
import { useRequest } from 'composables/useRequest.js'
import { useConstants } from 'composables/useConstants.js'
import FlashNotifications from 'controllers/vue_components/flash_toast'

const flash = ref(null)
const { axiosRequest } = useRequest(flash)
const { I18N, APPLICATION_NAME, parseLocale } = useConstants()

// ── State ──
const integration    = ref(null)
const url            = ref('')
const urlError       = ref('')
const loading        = ref(true)
const error          = ref(null)
const saving         = ref(false)
const removing       = ref(false)
const showRemoveModal = ref(false)

// ── Computed ──
const urlLooksValid = computed(() =>
  url.value.trim().startsWith('http://') || url.value.trim().startsWith('https://')
)

// ── Fetch ──
async function fetchIntegration() {
  loading.value = true
  error.value = null
  const res = await axiosRequest('GET', '/api/v1/integrations/external_payments_integrations', {}, false)
  loading.value = false
  if (!res.js_error && res.response.success) {
    integration.value = res.response.data.external_payments_integration
    url.value = integration.value?.payment_page_url ?? ''
  } else {
    error.value = 'Failed to load integration.'
  }
}

onMounted(fetchIntegration)

// ── Save / Update ──
async function submitUrl() {
  urlError.value = ''
  if (!url.value.trim()) {
    urlError.value = I18N.validations?.payment_page_url_cant_be_blank ?? 'URL is required'
    return
  }
  if (!urlLooksValid.value) {
    urlError.value = 'URL must start with https:// or http://'
    return
  }

  saving.value = true
  const method = integration.value ? 'PUT' : 'POST'
  const res = await axiosRequest(
    method,
    '/api/v1/integrations/external_payments_integrations',
    { external_payments_integration: { payment_page_url: url.value } },
    false,
  )
  saving.value = false

  if (!res.js_error && res.response.success) {
    integration.value = res.response.data.external_payments_integration
    url.value = integration.value.payment_page_url
    flash.value?.show(res.response.message, 'notice')
  } else {
    urlError.value = res.error?.data?.message ?? 'Failed to save. Check the URL and try again.'
  }
}

// ── Remove ──
async function removeIntegration() {
  removing.value = true
  const res = await axiosRequest('DELETE', '/api/v1/integrations/external_payments_integrations', {}, false)
  removing.value = false
  showRemoveModal.value = false
  if (!res.js_error && res.response.success) {
    integration.value = null
    url.value = ''
    flash.value?.show(res.response.message, 'notice')
  } else {
    flash.value?.show('Failed to remove integration.', 'alert')
  }
}
</script>

<style scoped>
.modal-enter-active { transition: opacity 0.2s ease; }
.modal-leave-active { transition: opacity 0.15s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
.modal-enter-active .modal-card { transition: transform 0.2s ease; }
.modal-enter-from  .modal-card  { transform: scale(0.96) translateY(8px); }
</style>
